import { api } from './api';
import { localDB } from './db';

const SYNC_INTERVAL = 15000;
let isSyncing = false;
const syncCallbacks = new Set();
let syncIntervalId = null;
let onlineHandler = null;
let initialized = false;

// Transient map for the duration of a single sync cycle to map
// temporary offline IDs to their newly created server IDs.
let tempIdMap = new Map();

/**
 * The SyncEngine replays queued offline actions when the network
 * is available. Deletes are processed before creates to avoid conflicts.
 */
export const syncEngine = {
  onSyncComplete(cb) {
    if (typeof cb === 'function') {
      syncCallbacks.add(cb);
      return () => syncCallbacks.delete(cb);
    }
  },

  async sync() {
    if (isSyncing || !navigator.onLine) return;
    
    try {
      isSyncing = true;
      tempIdMap.clear(); // Reset map at start of cycle
      
      console.log('[SyncEngine] Starting sync cycle...');
      const queue = await localDB.getQueue();
      if (queue.length === 0) {
        console.log('[SyncEngine] Queue empty, sync cycle skipped.');
        this.notifyListeners();
        return;
      }

      console.log(`[SyncEngine] Processing ${queue.length} pending actions...`);

      // Sort: deletes first, then updates, then creates.
      // Crucial: Folders must be created BEFORE Notes so FK mappings exist.
      const priority = { 
        DELETE_CLIP: 0, DELETE_NOTE: 0, DELETE_FOLDER: 0, DELETE_SNAP: 0, 
        UPDATE_CLIP: 1, UPDATE_FOLDER: 1, UPDATE_NOTE: 2, 
        CREATE_CLIP: 3, CREATE_FOLDER: 3, CREATE_NOTE: 4, CREATE_SNAP: 3 
      };
      const sorted = [...queue].sort((a, b) => (priority[a.type] ?? 9) - (priority[b.type] ?? 9));

      for (const action of sorted) {
        try {
          await this.processAction(action);
          await localDB.removeFromQueue(action.id);
        } catch (err) {
          console.error(`[SyncEngine] Failed to process action ${action.id}:`, err);
          
          // 4xx = permanent failure, remove from queue
          // 5xx / network = retry later
          if (err.status && err.status < 500) {
            await localDB.removeFromQueue(action.id);
          } else {
            break;
          }
        }
      }
    } finally {
      isSyncing = false;
      tempIdMap.clear();
      this.notifyListeners();
    }
  },

  notifyListeners() {
    syncCallbacks.forEach(cb => {
      try { cb(); } catch (err) {
        console.error('[SyncEngine] Error in sync callback:', err);
      }
    });
  },

  // Helper to remap foreign keys before sending to server
  _remapForeignKeys(data) {
    if (!data) return data;
    const remapped = { ...data };
    
    if (remapped.folder_id && tempIdMap.has(remapped.folder_id)) {
      console.log(`[SyncEngine] Remapping folder_id: ${remapped.folder_id} -> ${tempIdMap.get(remapped.folder_id)}`);
      remapped.folder_id = tempIdMap.get(remapped.folder_id);
    }
    
    if (remapped.parent_id && tempIdMap.has(remapped.parent_id)) {
      console.log(`[SyncEngine] Remapping parent_id: ${remapped.parent_id} -> ${tempIdMap.get(remapped.parent_id)}`);
      remapped.parent_id = tempIdMap.get(remapped.parent_id);
    }
    
    return remapped;
  },

  async processAction(action) {
    const { type } = action;

    switch (type) {
      // ── CLIPS ──────────────────────────────────────────────
      case 'CREATE_CLIP': {
        const res = await api.createClip(action.data);
        if (action.tempId) {
          await localDB.delete('clips', action.tempId);
        }
        await localDB.put('clips', res.data);
        break;
      }
      case 'UPDATE_CLIP': {
        const res = await api.updateClip(action.serverId, action.data);
        await localDB.put('clips', res.data);
        break;
      }
      case 'DELETE_CLIP': {
        await api.deleteClip(action.serverId);
        break;
      }

      // ── NOTES ──────────────────────────────────────────────
      case 'CREATE_NOTE': {
        const remappedData = this._remapForeignKeys(action.data);
        const res = await api.createNote(remappedData);
        if (action.tempId) {
          tempIdMap.set(action.tempId, res.data.id);
          await localDB.delete('notes', action.tempId);
        }
        await localDB.put('notes', res.data);
        break;
      }
      case 'UPDATE_NOTE': {
        // If we are updating a note that was just created in this sync cycle,
        // we need to use its new server ID.
        const targetId = tempIdMap.get(action.serverId) || action.serverId;
        const remappedData = this._remapForeignKeys(action.data);
        
        // If it's still a temp ID here, the create must have failed or hasn't run.
        if (String(targetId).startsWith('temp-')) {
            console.warn(`[SyncEngine] Cannot update ${targetId}, no server ID known.`);
            break; 
        }

        const res = await api.updateNote(targetId, remappedData);
        await localDB.put('notes', res.data);
        break;
      }
      case 'DELETE_NOTE': {
        const targetId = tempIdMap.get(action.serverId) || action.serverId;
        if (!String(targetId).startsWith('temp-')) {
          await api.deleteNote(targetId);
        }
        break;
      }

      // ── FOLDERS ────────────────────────────────────────────
      case 'CREATE_FOLDER': {
        const remappedData = this._remapForeignKeys(action.data);
        const res = await api.createFolder(remappedData);
        if (action.tempId) {
          // Record the mapping so child notes/folders know the real ID
          tempIdMap.set(action.tempId, res.data.id);
          await localDB.delete('folders', action.tempId);
        }
        await localDB.put('folders', res.data);
        break;
      }
      case 'UPDATE_FOLDER': {
        const targetId = tempIdMap.get(action.serverId) || action.serverId;
        const remappedData = this._remapForeignKeys(action.data);
        
        if (String(targetId).startsWith('temp-')) {
            console.warn(`[SyncEngine] Cannot update ${targetId}, no server ID known.`);
            break; 
        }

        const res = await api.updateFolder(targetId, remappedData);
        await localDB.put('folders', res.data);
        break;
      }
      case 'DELETE_FOLDER': {
        const targetId = tempIdMap.get(action.serverId) || action.serverId;
        if (!String(targetId).startsWith('temp-')) {
          await api.deleteFolder(targetId);
        }
        break;
      }

      // ── SNAPS ──────────────────────────────────────────────
      case 'CREATE_SNAP': {
        const fd = new FormData();
        fd.append('file', action.data.blob, 'snap.jpg');
        if (action.data.caption) fd.append('caption', action.data.caption);
        
        const res = await api.uploadSnap(fd);
        
        if (action.tempId) {
          await localDB.delete('snaps', action.tempId);
        }
        await localDB.put('snaps', res.data);
        break;
      }
      case 'DELETE_SNAP': {
        await api.deleteSnap(action.serverId);
        break;
      }

      default:
        console.warn(`[SyncEngine] Unknown action type: ${type}`);
    }
  },

  init() {
    if (initialized) return;
    initialized = true;

    onlineHandler = () => {
      console.log('[SyncEngine] Back online, syncing...');
      this.sync();
    };
    window.addEventListener('online', onlineHandler);

    syncIntervalId = setInterval(() => this.sync(), SYNC_INTERVAL);
    this.sync();
  },

  stop() {
    if (syncIntervalId) {
      clearInterval(syncIntervalId);
      syncIntervalId = null;
    }
    if (onlineHandler) {
      window.removeEventListener('online', onlineHandler);
      onlineHandler = null;
    }
    initialized = false;
  }
};
